import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Icon, Button, IconButton, Typography, Chip, Tooltip } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as SourceUtils from '../availableSources/SourceCardUtil';
import * as TargetUtils from '../targets/TargetCardUtil';
import * as ConnectorUtils from '../availableConnectorsV2/ConnectorCardUtil';
import {
  createJob,
  resetSourceState,
  updateLinkedSourceConfig,
  resetConnectorStateV2,
  updateLinkedConnectorConfig,
} from '../../api/client';
import { Alert } from '../popupAlerts';
import { History, PlayArrow, Autorenew, Remove, LinkOff, CloudUpload } from '@mui/icons-material';
import * as UIAction from '../../redux/actions/ui.actions';
import DataTable from '../../assets/data-table.svg';
import {
  fetchJob,
  fetchLinkedConnectors,
  fetchLinkedFlows,
  fetchLinkedSources,
  fetchLinkedTarget,
  fetchSupportedFlows,
  unlinkConnector,
  setActiveJobType,
  clearActiveJobType,
} from '../../redux/actions';
import { getListener, getSelectedFlow, getTenantId, isJobPending, getActiveJobType } from '../../redux/selectors';
import { disconnectLinkedSource, disconnectLinkedTarget, disconnectLinkedConnector } from '../../api/client';

const EntityActions = (props) => {
  const { entity, setLoading } = props;
  const entityId = useMemo(
    () => (entity.entityType === 'connector' ? entity.id : entity.entityType === 'source' ? entity.tap : entity.target),
    [entity]
  );
  const flowId = useSelector((state) => state.flows.selectedFlow);
  const flow = useSelector((state) => state.flows.supportedFlows[flowId]);
  const jobsEnabled = useSelector((state) => state.preferences.jobsEnabled);
  const writeJobsEnabled = useSelector((state) => state.preferences.writeJobsEnabled);
  const fullSyncJobs = useSelector((state) => state.preferences.fullSyncJobs);
  const fieldMapsEnabled = useSelector((state) => state.preferences.fieldMapsEnabled);
  const scheduleEnabled = useSelector((state) => state.preferences.scheduleEnabled);
  const isConnection = useSelector((state) => state.ui.isConnection);
  const localization = useSelector((state) => state.configuration.localization);
  const operation = useSelector((state) => state.ui.operation);
  const dispatch = useDispatch();
  const demo = useSelector((state) => state.preferences.demo);
  const { type } = entity;
  const reconnectEnabled = !demo && type && type === 'oauth';
  const hasGooglePicker = useMemo(() => entity.google_tokens !== undefined, [entity]);
  const lookupKey = useMemo(
    () => (entity.entityType === 'source' ? 'tap' : entity.entityType === 'target' ? 'target' : 'id'),
    [entity],
  );
  const lastJob = useSelector((state) => state.jobs.lastJob[`${entity.tenant_id}/${flowId}/${entity[lookupKey]}`]);
  const jobPending = useSelector((state) => isJobPending(state, lastJob?.s3_root, flowId, entity.tenant_id));
  const activeJobType = useSelector((state) => getActiveJobType(state, entity.tenant_id, flowId, entity[lookupKey]));
  
  // Show loading immediately when activeJobType is set (don't wait for jobPending)
  const readJobPending = activeJobType === 'read';
  const writeJobPending = activeJobType === 'write';
  const fullSyncJobPending = activeJobType === 'fullSync';
  
  // Google Picker loading state
  const [googlePickerLoading, setGooglePickerLoading] = useState(false);

  // Helper function to get tooltip message when buttons are disabled due to running job
  const getDisabledTooltip = () => {
    if (readJobPending) return localization?.quickActions?.readJobRunningTooltip || 'A read job is currently running';
    if (writeJobPending) return localization?.quickActions?.writeJobRunningTooltip || 'A write job is currently running'; 
    if (fullSyncJobPending) return localization?.quickActions?.fullSyncJobRunningTooltip || 'A full sync job is currently running';
    return '';
  };

  const onDisconnect = async () => {
    if (demo) {
      Alert({ type: 'info', text: `Sorry, you can't disconnect sources in demo mode!` });
      return;
    }

    setLoading('disconnect');
    const listener = getListener();
    if (entity.entityType === 'source') {
      await disconnectLinkedSource(entity.tap, flowId, entity.tenant_id);
      await dispatch(fetchLinkedSources());
      if (listener && listener.onSourceUnlinked) {
        listener.onSourceUnlinked(entity.tap, flowId);
      }
    }

    if (entity.entityType === 'target') {
      await disconnectLinkedTarget(entity.target, flowId, entity.tenant_id);
      await dispatch(fetchLinkedTarget());
      if (listener && listener.onTargetUnlinked) {
        listener.onTargetUnlinked(entity.target, flowId);
      }
    }

    if (entity.entityType === 'connector') {
      await disconnectLinkedConnector(entity.id, flowId, entity.tenant_id);
      dispatch(fetchLinkedConnectors());
      if (listener && listener.onConnectorUnlinked) {
        listener.onConnectorUnlinked(entity.id, flowId);
      }
    }

    dispatch(fetchLinkedFlows());
    dispatch(fetchSupportedFlows());
    setLoading(false);

    if (isConnection) UIAction.closeWidget();
    else UIAction.goBack();
  };

  const onReconnect = async () => {
    if (!reconnectEnabled) return;

    let entity_url = '';
    if (entity.entityType === 'source')
      entity_url = await SourceUtils.getTapUrl(entityId, entity, undefined, entity.tenant_id, entity.connection_name);
    else if (entity.entityType === 'connector')
      entity_url = await ConnectorUtils.getAuthUrl(entityId, entity, undefined, entity.tenant_id, entity.connection_name);
    else entity_url = await TargetUtils.getTargetUrl(entityId, entity);

    if (!entity_url) return;

    if (entity.entityType === 'source') {
      await updateLinkedSourceConfig(entity.tap, { isReconnecting: true }, undefined, entity.tenant_id);
      SourceUtils.createAndPollLink(entity_url, entity.label, entityId, entity.tenant_id, undefined, true);
    } else if (entity.entityType === 'connector') {
      await updateLinkedConnectorConfig(entityId, { isReconnecting: true }, undefined, entity.tenant_id);
      ConnectorUtils.createAndPollLink(entity_url, entity.label, entityId, entity.tenant_id, undefined, true);
    } else TargetUtils.createAndPollLink(entity_url, entity.label, entityId);

    const listener = getListener();
    if (listener && listener.onReconnect) {
      listener.onReconnect(entityId, flow);
    }
  };

  React.useEffect(() => {
    if (flowId && (!lastJob || lastJob.flow_id !== flowId)) {
      // If there is no last job, check if there is one
      dispatch(fetchJob(flowId, entity.tenant_id || getTenantId(), entity[lookupKey], entity.entityType));
    }
  }, [lastJob]);

  React.useEffect(() => {
    if (
      lastJob &&
      lastJob.status &&
      lastJob.s3_root &&
      lastJob.s3_root.split('/')[0] === entity.tenant_id &&
      !activeJobType && // Only set if no active job type is already set
      (lastJob.status.includes('RUNNING') || lastJob.status.includes('PENDING') || 
       (!lastJob.status.includes('FAILED') && !lastJob.status.includes('COMPLETE')))
    ) {
      // Try to detect job type from status or default to read
      let detectedJobType = 'read'; // Default
      if (lastJob.status.includes('WRITE') || lastJob.status.includes('write')) {
        detectedJobType = 'write';
      } else if (lastJob.status.includes('FULL_SYNC') || lastJob.status.includes('full_sync')) {
        detectedJobType = 'fullSync';
      }
      
      console.log(`Detected running job for ${entity.label}: ${lastJob.status} -> ${detectedJobType}`);
      dispatch(setActiveJobType(entity.tenant_id, flowId, entity[lookupKey], detectedJobType));
      handlePollJob(lastJob.s3_root);
    }
  }, [lastJob, activeJobType]);

  // No longer needed - we derive job states directly from Redux

  const handlePollJob = (jobRoot) => {
    if (entity.entityType === 'connector') {
      return ConnectorUtils.pollJob(jobRoot, flowId, entity.tenant_id || getTenantId(), entity.id);
    } else if (entity.entityType === 'source') {
      return SourceUtils.pollJob(jobRoot, flowId, entity.tenant_id || getTenantId(), entity.tap);
    } else {
      return TargetUtils.pollJob(jobRoot, flowId, entity.tenant_id || getTenantId(), entity.target);
    }
  };

  const onClickFullSyncJobs = (jobType = 'read') => async () => {
    if (demo) {
      Alert({ type: 'info', text: `Sorry, you can't run jobs in demo mode!` });
      return;
    }

    try {
      // Create the job
      dispatch(setActiveJobType(entity.tenant_id, flowId, entity[lookupKey], 'fullSync'));

      await (flow.version === 2
        ? resetConnectorStateV2(entityId, undefined, entity.tenant_id)
        : resetSourceState(entityId, undefined, entity.tenant_id));

      await runJob(jobType);

      Alert({ type: 'success', text: `Running Jobs for ${entity.label}` });
    } catch (err) {
      Alert({ type: 'error', text: `An error occurred while starting job. ${err.toString()}` });
      dispatch(clearActiveJobType(entity.tenant_id, flowId, entity[lookupKey]));
    }
  };

  const onClickJobs = (jobType = 'read') => async () => {
    if (demo) {
      Alert({ type: 'info', text: `Sorry, you can't run jobs in demo mode!` });
      return;
    }

    // Set loading

    try {
      // Create the job
      dispatch(setActiveJobType(entity.tenant_id, flowId, entity[lookupKey], jobType));

      await runJob(jobType);

      Alert({ type: 'success', text: `Running Jobs for ${entity.label}` });
    } catch (err) {
      Alert({ type: 'error', text: `An error occurred while starting job. ${err.toString()}` });
      dispatch(clearActiveJobType(entity.tenant_id, flowId, entity[lookupKey]));
    }
  };

  const runJob = async (jobType = 'read') => {
    const { job_id, s3_root } = await createJob(
      entity.entityType === 'source' ? entityId : undefined,
      undefined,
      entity.tenant_id,
      undefined,
      flow.version === 2 ? jobType : undefined,
    );

    // Start polling
    console.log(`Created job ${job_id} (${s3_root}) -> starting polling`);
    const future = handlePollJob(s3_root);

    future.then(() => {
      // Clear the active job type when job completes
      dispatch(clearActiveJobType(entity.tenant_id, flowId, entity[lookupKey]));
    });
  };

  const onOpenGooglePicker = async () => {
    if (demo) {
      Alert({ type: 'info', text: `Sorry, you can't use Google Picker in demo mode!` });
      return;
    }

    try {
      setGooglePickerLoading(true);
      await UIAction.openGoogleWidget(entity, getTenantId(), flowId);
    } catch (error) {
      console.error('Error opening Google Picker:', error);
      Alert({ type: 'error', text: `Failed to open Google Picker: ${error.message}` });
    } finally {
      setGooglePickerLoading(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      {(flow?.widgetOptions?.allowRunJobs || jobsEnabled) && (
        <ActionRow
          label={localization?.quickActions?.runJobsTitle || ((flow?.version ?? 1) === 1 ? 'Run Job' : 'Run Read Job')}
          description={
            localization?.quickActions?.runJobsSubtitle || `Run a ${(flow?.version ?? 1) === 1 ? '' : 'read '}job to sync your data with ${entity.label}.`
          }
          icon={() => <PlayArrow sx={{ fontSize: '16px' }} />}
          jobPending={readJobPending}
          disabled={readJobPending || writeJobPending || fullSyncJobPending}
          disabledTooltip={!readJobPending ? getDisabledTooltip() : ''}
          onClick={onClickJobs('read')}
        />
      )}
      {((flow?.version ?? 1) === 2 && (flow?.widgetOptions?.allowRunWriteJobs || writeJobsEnabled)) && (
        <ActionRow
          label={
            localization?.quickActions?.runWriteJobsTitle || 'Run Write Job'
          }
          description={
            localization?.quickActions?.runWriteJobsSubtitle || `Run a write job to sync your data with ${entity.label}.`
          }
          icon={() => <PlayArrow sx={{ fontSize: '16px' }} />}
          jobPending={writeJobPending}
          disabled={readJobPending || writeJobPending || fullSyncJobPending}
          disabledTooltip={!writeJobPending ? getDisabledTooltip() : ''}
          onClick={onClickJobs('write')}
        />
      )}
      {(flow?.widgetOptions?.allowFullSyncJobs || fullSyncJobs) && (
        <ActionRow
          label={localization?.quickActions?.runFullSyncJobTitle || 'Run a Full Sync Job'}
          description={
            localization?.quickActions?.runFullSyncJobSubtitle ||
            `Run a full sync job to clone all historical data from ${entity.label}.`
          }
          icon={() => <PlayArrow sx={{ fontSize: '16px' }} />}
          jobPending={fullSyncJobPending}
          disabled={readJobPending || writeJobPending || fullSyncJobPending}
          disabledTooltip={!fullSyncJobPending ? getDisabledTooltip() : ''}
          onClick={onClickFullSyncJobs('read')}
        />
      )}
      {fieldMapsEnabled &&
        ['source', 'connector'].includes(entity.entityType) &&
        entityId !== 'file' &&
        entityId !== 'google-sheets-beta' &&
        entityId !== 'google-drive' &&
        !((flow?.version ?? 1) === 2 && ['google-sheets', 'google-drive'].includes(entityId)) && (
          <ActionRow
            onClick={() => UIAction.editFieldMap(entity)}
            label={localization?.quickActions?.editFieldMapTitle || 'Edit field map'}
            description={
              localization?.quickActions?.editFieldMapSubtitle ||
              `Select the data you want to clone from ${entity.label}.`
            }
            icon={() => <Box component="img" src={DataTable} alt="Data table icon" width="16px" height="16px" />}
          />
        )}
      {(scheduleEnabled || flow?.widgetOptions?.allowSyncSchedule) && (
        <ActionRow
          onClick={() => UIAction.showSchedule(entity, entity.entityType)}
          label={localization?.quickActions?.editScheduleTitle || 'Edit schedule'}
          description={
            localization?.quickActions?.editScheduleSubtitle || 'Setup a schedule to automatically sync your data.'
          }
          icon={() => <History sx={{ fontSize: '16px' }} />}
        />
      )}
      {(hasGooglePicker || ['google-drive', 'google-sheets', 'google-sheets-beta'].includes(entity?.id ?? entity?.tap)) && (
        <ActionRow
          onClick={onOpenGooglePicker}
          label={localization?.quickActions?.openGooglePickerTitle || 'Select Files'}
          description={
            localization?.quickActions?.openGooglePickerSubtitle || `Select ${entity.label} files to import.`
          }
          icon={() => <CloudUpload sx={{ fontSize: '16px' }} />}
          googlePickerLoading={googlePickerLoading}
          disabled={googlePickerLoading || readJobPending || writeJobPending || fullSyncJobPending}
          disabledTooltip={!googlePickerLoading ? getDisabledTooltip() : ''}
        />
      )}
      {reconnectEnabled && (
        <ActionRow
          onClick={onReconnect}
          label={localization?.quickActions?.reconnectTitle || "Reconnect"}
          description={localization?.quickActions?.reconnectSubtitle || `Reconnect your ${entity.label} account.`}
          icon={() => <Autorenew sx={{ fontSize: '16px' }} />}
        />
      )}
      <ActionRow
        onClick={onDisconnect}
        disabled={operation === 'link'}
        color="error"
        label={localization?.quickActions?.unlinkTitle || 'Unlink'}
        description={localization?.quickActions?.unlinkSubtitle || `Unlink your ${entity.label} account.`}
        icon={() => <LinkOff sx={{ fontSize: '16px' }} />}
      />
    </Box>
  );
};

const ActionRow = (props) => {
  const { onClick, icon, label, description, color = 'default', disabled = false, jobPending, disabledTooltip, googlePickerLoading = false } = props;

  return (
    <Box
      display="flex"
      width="100%"
      justifyContent="space-between"
      py={3}
      pr={1}
      sx={{ borderBottom: ({ palette }) => `1px solid ${palette.common.divider}` }}
      alignItems="center"
    >
      <Box display="flex" flexDirection="column" gap={0.5}>
        <Box display="flex" gap={1}>
          <Typography variant="body1" fontWeight="500 ">
            {label}
          </Typography>
          {jobPending && (
            <Chip
              label={'RUNNING'}
              size="small"
              variant="outlined"
              sx={{
                color: '#FA5240',
                borderColor: '#FDBBB4',
                fontSize: '10px',
                height: '18px',
                fontWeight: '600',
                marginLeft: '4px',
              }}
            />
          )}
          {googlePickerLoading && (
            <Chip
              label={'VERIFYING'}
              size="small"
              variant="outlined"
              sx={{
                color: '#27ae60',
                borderColor: '#b6e7c9',
                fontSize: '10px',
                height: '18px',
                fontWeight: '600',
                marginLeft: '4px',
              }}
            />
          )}
        </Box>
        <Typography variant="body2" color="text.lightish">
          {description}
        </Typography>
      </Box>
      <Tooltip 
        title={disabled && disabledTooltip ? disabledTooltip : ''} 
        placement="top" 
        disableHoverListener={!disabled || !disabledTooltip}
      >
        <span>
          <IconButton role='button' aria-label={label} variant="default" color={color} onClick={onClick} disabled={disabled}>
            {(jobPending || googlePickerLoading) ? <CircularProgress size={12} /> : typeof icon === 'function' && icon()}
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default EntityActions;
